import React, { useEffect } from 'react';
import { Input, Select } from '@attentive-platform/stem-ui';

import { ModifierInputStyles as S } from './styles';

const Chip = ({ label, on, onClick, disabled }) => {
  if (disabled) return <S.ChipDisabled>{label}</S.ChipDisabled>;
  const Cmp = on ? S.ChipOn : S.ChipOff;
  return <Cmp onClick={onClick}>{label}</Cmp>;
};

const rawOptions = modifier => modifier.answer_options || modifier.modifier_answer_options || [];

const optionLabel = opt => {
  if (opt == null) return '';
  if (typeof opt === 'string' || typeof opt === 'number') return String(opt);
  return String(opt.label ?? opt.value ?? opt.name ?? '');
};

/** Prefer answer_options[].default, else modifier.default when it matches an option. */
const resolveDefaultOption = (modifier, options) => {
  const flagged = options.find(o => o && typeof o === 'object' && o.default);
  if (flagged != null) {
    const label = optionLabel(flagged);
    return label || null;
  }
  if (modifier.default == null || modifier.default === '') return null;
  const want = String(modifier.default);
  const match = options.find(o => optionLabel(o) === want);
  return match != null ? optionLabel(match) : null;
};

export { resolveDefaultOption };

const ModifierInput = ({ modifier, value, onChange, readOnly }) => {
  const answers = value || { selected_options: [], text_value: '' };
  const options = rawOptions(modifier);
  const labels = options.map(optionLabel).filter(Boolean);
  const defaultOption = resolveDefaultOption(modifier, options);
  const storedOptions = answers.selected_options || [];
  const selectedOptions =
    storedOptions.length > 0
      ? storedOptions
      : value == null && defaultOption != null
        ? [defaultOption]
        : storedOptions;

  const setOptions = next => onChange({ selected_options: next });
  const setText = text => onChange({ text_value: text });

  // Seed draft from API default only when this modifier has never been answered.
  useEffect(() => {
    if (readOnly || defaultOption == null || value != null) return;
    onChange({ selected_options: [defaultOption] });
  }, [readOnly, defaultOption, value, onChange]);

  const bodyParts = (
    <S.Section>
      <S.HeaderRow>
        <S.Title>{modifier.title}</S.Title>
        {modifier.complexity === 'advanced' && (
          <S.AdvancedPill>advanced — may affect turnaround &amp; price</S.AdvancedPill>
        )}
      </S.HeaderRow>
      {modifier.body && <S.BodyText>{modifier.body}</S.BodyText>}
    </S.Section>
  );

  if (modifier.modifier_type === 'multi_select') {
    return (
      <S.Section>
        {bodyParts}
        <S.ChipRow>
          {labels.map(opt => {
            const on = selectedOptions.includes(opt);
            return (
              <Chip
                key={opt}
                label={opt}
                on={on}
                disabled={readOnly}
                onClick={
                  readOnly
                    ? undefined
                    : () => {
                        const next = on
                          ? selectedOptions.filter(x => x !== opt)
                          : [...selectedOptions, opt];
                        setOptions(next);
                      }
                }
              />
            );
          })}
        </S.ChipRow>
      </S.Section>
    );
  }

  if (modifier.modifier_type === 'dropdown') {
    const items = labels.map(o => ({ label: o, value: o }));
    const selected = selectedOptions[0] || '';
    return (
      <S.Section>
        {bodyParts}
        <S.SelectWrap>
          <Select
            size='small'
            variant='std-filled'
            items={items}
            value={selected}
            onChange={v => setOptions([v])}
            disabled={readOnly}
            placeholder='Choose one'
          />
        </S.SelectWrap>
      </S.Section>
    );
  }

  if (modifier.modifier_type === 'subjective') {
    return (
      <S.Section>
        {bodyParts}
        <S.InputWrap>
          <Input
            size='large'
            multiline
            minRows={3}
            value={answers.text_value}
            onChange={e => setText(e.target.value)}
            placeholder='Type your answer'
            disabled={readOnly}
          />
        </S.InputWrap>
      </S.Section>
    );
  }

  if (modifier.modifier_type === 'example') {
    // Scope items render examples via ExampleCards; keep a minimal fallback here.
    return bodyParts;
  }

  return bodyParts;
};

export default ModifierInput;
